import * as React from "react";
import { ChevronDown, Check } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../primitives/popover";
import { cn } from "../../lib/cn";
import type { ComposerMode } from "./types";

/**
 * Composer.Header — top row above the textarea.
 *
 * Three slots:
 *   - left: ModeTrigger ("Ando · Standard ▾") — Radix Popover with mode list
 *   - right: ⌘⏎ keyboard hint that fades in on focus-within
 *
 * v18 mockup pattern (Claude/Perplexity 2026): model settings live above
 * the textarea, not in the action bar — keeps the bottom bar focused on
 * send affordance + active capabilities only.
 */

export interface ComposerHeaderProps {
  /** Available modes (Ando · Standard, Vendite Q2, Audit Q1, …). */
  modes?: ComposerMode[];
  /** Active mode id — controlled. */
  activeModeId?: string;
  onModeChange?: (id: string) => void;
  /** Click "Gestisci modes →" — opens Studio drawer (parent-owned). */
  onManageModes?: () => void;
  /** Keyboard hint copy (defaults to `⌘⏎`). Hide with `kbdHint={null}`. */
  kbdHint?: React.ReactNode;
  /**
   * v1 parity (May 23, 2026): lock the mode trigger once the conversation
   * has ≥1 user message to prevent mid-conversation mode drift. The popover
   * cannot open while disabled (Radix forwards `disabled` to the trigger).
   */
  disabled?: boolean;
  className?: string;
}

const DEFAULT_MODES: ComposerMode[] = [
  { id: "standard", label: "Ando · Standard" },
];

export const ComposerHeader = React.forwardRef<HTMLDivElement, ComposerHeaderProps>(
  (
    {
      modes = DEFAULT_MODES,
      activeModeId = "standard",
      onModeChange,
      onManageModes,
      kbdHint = "⌘⏎",
      disabled = false,
      className,
    },
    ref
  ) => {
    const active = modes.find((m) => m.id === activeModeId) ?? modes[0];
    const [open, setOpen] = React.useState(false);

    return (
      <div
        ref={ref}
        className={cn(
          "flex min-h-[20px] items-center justify-between gap-2 px-3 pt-2.5",
          className
        )}
      >
        <Popover open={disabled ? false : open} onOpenChange={(o) => !disabled && setOpen(o)}>
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              aria-haspopup="menu"
              aria-expanded={open}
              aria-disabled={disabled || undefined}
              aria-label="Modello e potenza ricerca"
              title={disabled ? "Modalità bloccata dopo il primo messaggio" : undefined}
              className={cn(
                "group -ml-1 inline-flex items-center gap-1.5 rounded-md px-1.5 py-0.5",
                "font-medium text-[12px] text-ink-1 leading-none",
                "transition-colors duration-150 ease-out",
                "hover:bg-black/[0.04] hover:text-ink-1",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45 focus-visible:ring-offset-1",
                disabled && "cursor-not-allowed opacity-60 hover:bg-transparent hover:text-ink-3"
              )}
            >
              <span className="leading-none">
                {active?.label.split("·")[0]?.trim() ?? "Ando"}
                {active?.label.includes("·") ? (
                  <span className="font-normal text-ink-3">
                    {" · "}
                    {active.label.split("·").slice(1).join("·").trim()}
                  </span>
                ) : null}
              </span>
              <ChevronDown
                size={9}
                strokeWidth={2.5}
                className={cn(
                  "text-ink-4 transition-transform duration-[220ms] ease-smooth",
                  "group-hover:text-ink-2",
                  open && "rotate-180"
                )}
              />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            sideOffset={8}
            className="w-64 p-1.5"
          >
            <div role="menu" className="flex flex-col gap-0.5">
              {modes.map((m) => {
                const isActive = m.id === activeModeId;
                return (
                  <button
                    key={m.id}
                    type="button"
                    role="menuitemradio"
                    aria-checked={isActive}
                    onClick={() => {
                      onModeChange?.(m.id);
                      setOpen(false);
                    }}
                    className={cn(
                      "flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-xs",
                      "transition-colors duration-150 ease-out",
                      "hover:bg-ando-orange/[0.08]",
                      isActive ? "font-semibold text-ink-1" : "text-ink-2"
                    )}
                  >
                    <span className="flex min-w-0 flex-col gap-0.5">
                      <span className="truncate leading-none">{m.label}</span>
                      {m.description ? (
                        <span className="truncate text-[10px] text-ink-3">
                          {m.description}
                        </span>
                      ) : null}
                    </span>
                    {isActive ? (
                      <Check
                        size={12}
                        strokeWidth={2.5}
                        className="shrink-0 text-ando-orange"
                      />
                    ) : null}
                  </button>
                );
              })}
            </div>
            {onManageModes ? (
              <>
                <div className="my-1.5 h-px bg-hairline" />
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    onManageModes();
                  }}
                  className={cn(
                    "flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-ink-2 text-xs",
                    "transition-colors duration-150 ease-out",
                    "hover:bg-black/[0.04] hover:text-ink-1"
                  )}
                >
                  <span>Gestisci modes</span>
                  <span aria-hidden="true">→</span>
                </button>
              </>
            ) : null}
          </PopoverContent>
        </Popover>

        {kbdHint ? (
          <span
            className={cn(
              // Quiet kbd hint — reveals on focus-within (CSS state on Root)
              "font-mono text-[10px] text-ink-4 tabular-nums",
              "translate-x-1 opacity-0 transition-[opacity,transform] delay-100 duration-200 ease-out",
              "[.composer-card:focus-within_&]:translate-x-0 [.composer-card:focus-within_&]:opacity-100",
              // Fallback when not nested in composer-card (storybook)
              "group-focus-within/composer:opacity-100"
            )}
            aria-hidden="true"
          >
            {kbdHint}
          </span>
        ) : null}
      </div>
    );
  }
);
ComposerHeader.displayName = "Composer.Header";
